import { forwardRef, type InputHTMLAttributes } from 'react'

import { clsx } from 'clsx'

/**
 * Toggle Component
 *
 * On/off switch with the following features:
 * - Label positioning (left or right)
 * - Disabled state
 * - Loading state
 * - Size variants (sm, md, lg)
 * - Overflow handling
 * - All dimensions properly defined
 *
 * Design Requirements:
 * - Can work standalone or within FormField
 * - Label aligns to 72px line
 * - All dimensions defined (height, padding, margin, border, outline)
 * - Text overflow with ellipsis
 */

export interface ToggleProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'type' | 'onChange'> {
  /** Checked state */
  checked?: boolean
  /** On change handler */
  onChange?: (checked: boolean) => void
  /** Label text */
  label?: string
  /** Label position */
  labelPosition?: 'left' | 'right'
  /** Size variant */
  size?: 'sm' | 'md' | 'lg'
  /** Loading state */
  isLoading?: boolean
  /** Custom className */
  className?: string
  /** @internal - Not for use, filtered out from DOM */
  hideMessage?: boolean
}

// =============================================================================
// Size styles
// =============================================================================

export const TOGGLE_SIZE_STYLES = {
  sm: { width: '36px', height: '20px', toggleSize: '12px', translateX: '16px', fontSize: '0.875rem', lineHeight: '20px' },
  md: { width: '44px', height: '24px', toggleSize: '16px', translateX: '20px', fontSize: '1rem', lineHeight: '24px' },
  lg: { width: '52px', height: '28px', toggleSize: '20px', translateX: '24px', fontSize: '1rem', lineHeight: '28px' },
}

// =============================================================================
// LoadingSpinner
// =============================================================================

function LoadingSpinner(): JSX.Element {
  return (
    <div className="flex items-center justify-center w-full h-full" style={{ margin: 0, padding: 0 }}>
      <div
        className="animate-spin rounded-full border-2 border-primary border-t-transparent"
        style={{ width: '12px', height: '12px', margin: 0, padding: 0 }}
      />
    </div>
  )
}

// =============================================================================
// ToggleTrack
// =============================================================================

interface ToggleTrackProps {
  checked: boolean
  isDisabled: boolean
  isLoading: boolean
  currentSize: typeof TOGGLE_SIZE_STYLES['md']
  onChange?: (checked: boolean) => void
}

function ToggleTrack({ checked, isDisabled, isLoading, currentSize, onChange }: ToggleTrackProps): JSX.Element {
  return (
    <div
      aria-checked={checked}
      className={clsx(
        'rounded-full transition-colors duration-200 cursor-pointer overflow-hidden',
        'border border-transparent',
        {
          'bg-primary': checked && !isDisabled,
          'bg-muted-foreground/50': checked && isDisabled,
          'bg-muted': !checked && !isDisabled,
          'bg-accent': !checked && isDisabled,
          'cursor-not-allowed': isDisabled,
        }
      )}
      role="switch"
      style={{ width: currentSize.width, height: currentSize.height, minWidth: currentSize.width, minHeight: currentSize.height, margin: 0, padding: '3px', outline: 'none', borderWidth: '1px' }}
      tabIndex={0}
      onClick={() => { if (!isDisabled) { onChange?.(!checked) } }}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !isDisabled) {
          e.preventDefault()
          onChange?.(!checked)
        }
      }}
    >
      <div
        className={clsx('rounded-full transition-transform duration-200 overflow-hidden', 'bg-white dark:bg-foreground', { 'translate-x-0': !checked })}
        style={{ width: currentSize.toggleSize, height: currentSize.toggleSize, transform: checked ? `translateX(${currentSize.translateX})` : 'translateX(0)', margin: 0, padding: 0, border: 0, outline: 'none' }}
      >
        {isLoading && <LoadingSpinner />}
      </div>
    </div>
  )
}

// =============================================================================
// LabelSpan
// =============================================================================

interface LabelSpanProps {
  label: string
  isDisabled: boolean
  fontSize: string
  lineHeight: string
}

function LabelSpan({ label, isDisabled, fontSize, lineHeight }: LabelSpanProps): JSX.Element {
  return (
    <span
      className={clsx('text-foreground truncate', { 'text-muted-foreground': isDisabled })}
      style={{ fontSize, lineHeight, margin: 0, padding: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
    >
      {label}
    </span>
  )
}

// =============================================================================
// Toggle
// =============================================================================

interface ToggleElementProps {
  checked: boolean
  isDisabled: boolean
  isLoading: boolean
  id?: string
  forwardRef: React.Ref<HTMLInputElement>
  inputProps: Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange' | 'checked' | 'disabled' | 'id'>
  onChange?: (checked: boolean) => void
  currentSize: typeof TOGGLE_SIZE_STYLES['md']
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void
}

function ToggleElement(p: ToggleElementProps): JSX.Element {
  return (
    <div className="relative inline-block flex-shrink-0">
      <input
        ref={p.forwardRef}
        aria-checked={p.checked}
        checked={p.checked}
        className="sr-only"
        disabled={p.isDisabled}
        id={p.id}
        role="switch"
        style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', borderWidth: 0 }}
        type="checkbox"
        onChange={p.handleChange}
        {...p.inputProps}
      />
      <ToggleTrack
        checked={p.checked}
        currentSize={p.currentSize}
        isDisabled={p.isDisabled}
        isLoading={p.isLoading}
        onChange={p.onChange}
      />
    </div>
  )
}

export const Toggle = forwardRef<HTMLInputElement, ToggleProps>(
  (
    {
      checked = false,
      onChange,
      label,
      labelPosition = 'right',
      size = 'md',
      isLoading = false,
      disabled: isDisabledProp = false,
      className,
      id,
      hideMessage: _hideMessage,
      ...props
    },
    ref,
  ) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
      if (!isDisabledProp && !isLoading) {
        onChange?.(e.target.checked)
      }
    }

    const currentSize = TOGGLE_SIZE_STYLES[size]
    const isDisabled = isDisabledProp || isLoading

    return (
      <ToggleWithLabel
        className={className}
        currentSize={currentSize}
        forwardRef={ref}
        handleChange={handleChange}
        id={id}
        inputProps={props}
        isChecked={checked}
        isDisabled={isDisabled}
        isLoading={isLoading}
        label={label}
        labelPosition={labelPosition}
        onChange={onChange}
      />
    )
  },
)

interface ToggleWithLabelProps {
  isChecked: boolean
  label?: string
  labelPosition: 'left' | 'right'
  isDisabled: boolean
  isLoading: boolean
  className?: string
  id?: string
  forwardRef: React.Ref<HTMLInputElement>
  inputProps: Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange' | 'checked' | 'disabled' | 'id'>
  currentSize: typeof TOGGLE_SIZE_STYLES['md']
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onChange?: (checked: boolean) => void
}

function ToggleWithLabel(p: ToggleWithLabelProps): JSX.Element {
  const toggleElement = (
    <ToggleElement
      checked={p.isChecked}
      currentSize={p.currentSize}
      forwardRef={p.forwardRef}
      handleChange={p.handleChange}
      id={p.id}
      inputProps={p.inputProps}
      isDisabled={p.isDisabled}
      isLoading={p.isLoading}
      onChange={p.onChange}
    />
  )
  if (!p.label) { return toggleElement }
  return (
    <label
      className={clsx('inline-flex items-center gap-3 cursor-pointer overflow-hidden', { 'cursor-not-allowed': p.isDisabled }, p.className)}
      htmlFor={p.id}
      style={{ margin: 0, padding: 0, lineHeight: p.currentSize.lineHeight }}
    >
      {p.labelPosition === 'left' && (
        <LabelSpan fontSize={p.currentSize.fontSize} isDisabled={p.isDisabled} label={p.label} lineHeight={p.currentSize.lineHeight} />
      )}
      {toggleElement}
      {p.labelPosition === 'right' && (
        <LabelSpan fontSize={p.currentSize.fontSize} isDisabled={p.isDisabled} label={p.label} lineHeight={p.currentSize.lineHeight} />
      )}
    </label>
  )
}

Toggle.displayName = 'Toggle'

export default Toggle
